import { addDays, format, isBefore, startOfDay } from 'date-fns';

/**
 * Parse date string safely for Safari compatibility.
 * Safari requires proper ISO format, so we normalize common API date formats.
 *
 * Handles:
 * - "YYYY-MM-DD HH:mm:ss UTC" -> "YYYY-MM-DDTHH:mm:ss.000Z"
 * - Standard ISO strings
 * - Other date formats (fallback)
 */
export const parseDateSafely = (dateString: string): Date => {
	if (!dateString) return new Date(NaN);

	// Safari requires proper ISO format, convert "YYYY-MM-DD HH:mm:ss UTC" to "YYYY-MM-DDTHH:mm:ss.000Z"
	const normalizedDate = dateString.replace(' UTC', '.000Z').replace(' ', 'T');
	const date = new Date(normalizedDate);

	// Fallback to original string if normalized parsing fails
	if (isNaN(date.getTime())) {
		return new Date(dateString);
	}

	return date;
};

/**
 * Check if a date is valid (not NaN/Invalid Date)
 */
export const isValidDate = (date: Date): boolean => {
	return !isNaN(date.getTime());
};

/**
 * Get month/year string like "January 2025" from a date string.
 * Returns "Unknown Date" for invalid dates.
 */
export const getMonthYear = (dateString: string): string => {
	const date = parseDateSafely(dateString);

	if (!isValidDate(date)) {
		return 'Unknown Date';
	}

	return format(date, 'MMMM yyyy');
};

/**
 * Format a date string safely, returning a fallback for invalid dates.
 */
export const formatDateSafely = (dateString: string, formatStr: string, fallback = 'Unknown Date'): string => {
	const date = parseDateSafely(dateString);

	if (!isValidDate(date)) {
		return fallback;
	}

	return format(date, formatStr);
};

export function toTimelessDateString(date: Date): string;
export function toTimelessDateString(date: null | undefined): null;
export function toTimelessDateString(date?: Date | null): string | null;
export function toTimelessDateString(date?: Date | null): string | null {
	if (!date) {
		return null;
	}
	return format(date, 'yyyy-MM-dd');
}

export const dateIsBeforeToday = (date: Date) => {
	return isBefore(startOfDay(date), startOfDay(new Date()));
};

export const dateIsBeforeTomorrow = (date: Date) => {
	return isBefore(startOfDay(date), startOfDay(addDays(new Date(), 1)));
};

// NOTE month is in format MM-YYYY (03-2025 is March 2025)
// and we want to convert it to a date object
export const convertMonthToDate = (monthYear: string) => {
	const [monthPart, yearPart] = monthYear.split('-');
	const year = parseInt(yearPart);
	const month = parseInt(monthPart);
	return new Date(year, month - 1, 1);
};
